/**
 * /from-meeting-notes-to-decisions — the honest PIVOT page for the
 * meeting-notes / note-taker traffic (read ai meeting notes, notion ai
 * meeting notes, meeting notes taker, etc.). The brief's explicit angle:
 * meeting notes are RAW INPUT; Draftlize turns the decisions inside them
 * into living product context. We do NOT pretend to be a transcription or
 * note-taking tool — we say "keep your notetaker" and pick up where it ends.
 *
 * English-only + inline. Chinese variant in FromMeetingNotesToDecisionsZh.tsx.
 */
import { MarketingShell } from './MarketingShell';

export function FromMeetingNotesToDecisions() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/from-meeting-notes-to-decisions"
      documentTitle="From Meeting Notes to Product Decisions · Draftlize"
      documentDescription="AI meeting notes capture the conversation. But the decisions inside them get buried and go stale. How to turn raw meeting notes into living product context your team and AI agents can use."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guide · Meeting notes</span>
          <h1 className="serif" style={{ fontSize: 'clamp(38px, 4.8vw, 60px)', lineHeight: 1.04, margin: '12px 0 24px' }}>
            From meeting notes<br/>
            <em>to product decisions that stay alive.</em>
          </h1>
          <p className="lede">
            AI notetakers — Granola, Otter, Fireflies, Read, Notion AI — got very good at one thing: capturing what was said. You end a call and a tidy transcript and summary appear. That&apos;s genuinely useful, and you should keep using them. But a meeting summary is <em>raw input</em>, not the output. The thing that actually mattered in that meeting — the decision you reached, and why — gets buried in a wall of notes, lands in a doc nobody reopens, and is stale within weeks. This page is about the step after the notetaker: turning the decisions inside your meeting notes into product context that stays current.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">The notetaker captured everything. <em>That&apos;s the problem.</em></h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink-1)', margin: '0 0 18px' }}>
            A great meeting summary is comprehensive — which means the one decision that the meeting existed to make is sitting in paragraph nine, weighted the same as the small talk and the tangent about lunch. Capture isn&apos;t the bottleneck anymore; <em>extraction</em> is. The valuable residue of a meeting is small: a few decisions, the reasons behind them, and who owns the follow-up. Everything else is context you&apos;ll never read again.
          </p>
          <p style={{ fontSize: 17, lineHeight: 1.7, color: 'var(--ink-1)', margin: 0 }}>
            And even when someone does pull the decision out, it lands in a notes doc — flat text, disconnected from the spec it affects and the earlier decision it reverses. Three weeks later the product has moved, that decision is quietly outdated, and the next meeting re-litigates something you already settled, because no one could find what was decided or tell whether it still held.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Raw input vs living context</span>
            <h2 className="serif">Where the notetaker ends, <em>and the work begins.</em></h2>
          </div>
          <p>Your notetaker and Draftlize do different jobs. One captures the conversation; the other keeps the decisions inside it alive. Keep both.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th>AI notetaker</th>
              <th className="ours">Draftlize</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Job</td><td>Capture what was said</td><td className="ours yes">Keep what was decided current</td></tr>
            <tr><td>Output</td><td className="partial">Transcript + summary</td><td className="ours yes">Decision cards in a graph</td></tr>
            <tr><td>A decision reverses an earlier one</td><td className="no">Two unrelated notes</td><td className="ours yes">Linked; the old one flags stale</td></tr>
            <tr><td>Three weeks later</td><td className="no">Buried in a doc</td><td className="ours yes">Addressable, current or flagged</td></tr>
            <tr><td>An AI agent can use it</td><td className="partial">Reads flat text</td><td className="ours yes">Reads + writes via MCP</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Turn the decisions into <em>living context.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Keep your notetaker for the capture. The move is what happens to the few decisions inside the notes — in Draftlize they stop being lines in a summary and become a graph that stays true.
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Decisions become cards, not lines</h3>
            <p>Paste a meeting summary into Draftlize and the decisions inside it become typed cards — the decision, the rationale, the alternatives weighed, the owner. The hour of conversation collapses to the handful of things that actually have to survive it, each addressable by ID instead of buried at paragraph nine.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">A new decision flags the one it reverses</h3>
            <p>This week&apos;s meeting changes a call you made last month. Because the decisions are linked, the new one turns the old one — and every spec built on it — stale automatically. The next meeting doesn&apos;t re-litigate settled ground, because what was decided, and whether it still holds, is right there.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Your AI agent reads what was decided</h3>
            <p>The decisions from your meetings become context Claude Code or Cursor reads over MCP — so the agent drafting your next spec already knows what the team decided on Tuesday, and why, instead of starting from a transcript it has to re-read every time.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Your notetaker captures the meeting perfectly. It just can&apos;t tell you, three weeks later, which of the decisions inside it are still true.
        </blockquote>
        <cite>Keep the notetaker. Let Draftlize keep the decisions from it alive.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'Does Draftlize record or transcribe meetings?', a: 'No — and we will not pretend to. Tools like Granola, Otter, Fireflies, Read, and Notion AI are excellent at recording and transcribing; keep using the one you like. Draftlize picks up after the notes exist: it turns the decisions inside them into structured cards that stay current.' },
            { q: 'What is wrong with just keeping meeting notes in a doc?', a: 'Nothing, for a record of the conversation. The problem is that the decisions — the part that matters — sit as flat text, disconnected from the specs they affect and the earlier decisions they reverse. So they go stale silently, and the next meeting re-decides things you already settled.' },
            { q: 'How do I get decisions out of my meeting notes into Draftlize?', a: 'Paste the summary your notetaker produced, and capture the decisions inside it as cards — the decision, the rationale, the owner. The point is not to store the whole transcript; it is to keep the few decisions that have to outlive the meeting addressable and current.' },
            { q: 'How is this different from an AI meeting notes tool with "action items"?', a: 'Action items are tasks that get checked off. Decisions are different — they have rationale, dependencies, and a lifespan, and they go stale when something upstream changes. Draftlize tracks that: when a later decision reverses an earlier one, the dependents flag stale, which an action-item list does not do.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Turn your meeting notes into decisions <em>— free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Keep your notetaker. Drop the summary into Draftlize and let the decisions inside it become living cards — linked, current, and readable by your AI agent — so the next meeting builds on what you decided instead of re-deciding it.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
